import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo, siteInfo } from '@/mocks/siteData';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tianmeimahua.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.tianmeimahua.com/about' },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbList) }} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium">关于我们</span>
            </nav>

            <div className="text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900">
                关于天美麻花星空影视
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.intro}
              </p>
            </div>

            <div className="rounded-lg bg-background-100 p-6 md:p-8 mb-12 text-center">
              <p className="text-sm text-foreground-500 mb-2">我们的使命</p>
              <p className="text-lg md:text-xl font-bold font-heading text-foreground-900">
                {aboutInfo.mission}
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 text-center">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {aboutInfo.values.map((v) => (
                  <div key={v.title} className="rounded-lg bg-background-100 p-5 text-center">
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-600">{v.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 text-center">
                发展历程
              </h2>
              <div className="space-y-0">
                {aboutInfo.milestones.map((m, idx) => (
                  <div key={m.year} className="flex items-start gap-4 pb-6 last:pb-0">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {m.year}
                      </div>
                      {idx < aboutInfo.milestones.length - 1 && (
                        <div className="w-0.5 flex-1 bg-background-200 mt-1" />
                      )}
                    </div>
                    <div className="pt-2">
                      <p className="text-sm text-foreground-700 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutInfo.testimonials.map((t) => (
                  <div key={t.name} className="rounded-lg bg-background-100 p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`} />
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">{t.content}</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600">
                        {t.name[0]}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 text-center">
                联系我们
              </h2>
              <div className="rounded-lg bg-background-100 p-6 md:p-8 max-w-lg mx-auto">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">客服电话</p>
                      <p className="text-sm font-medium text-foreground-900">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">公司地址</p>
                      <p className="text-sm font-medium text-foreground-900">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}